import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Check } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/orders/${id}`)
      .then((r) => setOrder(r.data))
      .catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div data-testid="order-not-found">
        <PageHeader eyebrow="Order" title="We could not find that order." body="It may have been placed in a different session." />
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16">
          <Link to="/shop" data-testid="order-not-found-shop-link" className="inline-block border border-stone-900 px-8 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-900 transition-colors duration-300 hover:bg-stone-900 hover:text-stone-50">
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="order-success-page">
      <PageHeader eyebrow="Order received" title="Thank you." />
      <section className="mx-auto max-w-3xl px-4 pb-24 sm:px-8 lg:px-16">
        <Reveal>
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-900">
            <Check className="h-6 w-6 text-stone-50" strokeWidth={1.5} />
          </div>
          {order ? (
            <div className="mt-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">Order number</p>
              <p data-testid="order-number" className="mt-2 font-serif text-4xl font-light text-stone-900">
                {order.order_number}
              </p>
              <div className="mt-8 space-y-3 border-t border-stone-900/10 pt-8 text-sm">
                {order.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-stone-700">
                    <span>
                      {i.name} <span className="font-mono text-xs text-stone-500">× {i.qty}</span>
                    </span>
                    <span className="font-mono">£{(i.price * i.qty).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-stone-900/10 pt-4">
                  <span className="font-medium text-stone-900">Total</span>
                  <span data-testid="order-total" className="font-serif text-2xl font-light text-stone-900">
                    £{order.total.toFixed(2)}
                  </span>
                </div>
              </div>
              <p className="mt-8 border border-emerald-900/30 bg-[#EEF3EF] px-6 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-900">
                This was a demo order — no payment was taken.
              </p>
            </div>
          ) : (
            <p className="mt-8 text-sm text-stone-500">Loading your order…</p>
          )}
          <div className="mt-12 flex flex-wrap gap-4">
            <Link
              to="/shop"
              data-testid="order-success-shop-link"
              className="bg-stone-900 px-8 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-50 transition-colors duration-300 hover:bg-emerald-900"
            >
              Continue Shopping
            </Link>
            <Link
              to="/"
              data-testid="order-success-home-link"
              className="border border-stone-900 px-8 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-900 transition-colors duration-300 hover:bg-stone-900 hover:text-stone-50"
            >
              Back Home
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
